/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import LocationGate from "../../lib/LocationGate";
import {
  NavLink, Outlet, Link, useNavigate, useParams,
} from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Gauge, Users, GraduationCap, ChartBar, Certificate, Palette, Receipt,
  FileText, ShieldCheck, SignOut, ArrowRight, Lightning, Buildings,
  Envelope, LockKey, EyeSlash, Eye, DownloadSimple,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = "#0B2B66";
const GOLD = "#C9A227";
const GREEN = "#2D5A27";
const RED = "#B12A1E";

const papi = axios.create({ baseURL: API });
papi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("rescigo_partner_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Error";
};

const PARTNER_USER_KEY = "rescigo_partner_user";
const getPartnerUser = () => {
  try { return JSON.parse(localStorage.getItem(PARTNER_USER_KEY) || "null"); }
  catch { return null; }
};
const setPartnerUser = (u) => localStorage.setItem(PARTNER_USER_KEY, JSON.stringify(u));
const clearPartnerAuth = () => {
  localStorage.removeItem("rescigo_partner_token");
  localStorage.removeItem(PARTNER_USER_KEY);
};

// ============================================================================
// Partner Login (email-OTP + magic-link)
// ============================================================================
export function PartnerLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState("creds");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [ttl, setTtl] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const magicConsumed = React.useRef(false);

  // Auto-magic login on mount if ?magic= is in URL
  useEffect(() => {
    if (localStorage.getItem("rescigo_partner_token")) {
      const cached = (() => {
        try { return JSON.parse(localStorage.getItem("rescigo_partner_user") || "null"); }
        catch { return null; }
      })();
      const next = new URL(window.location.href).searchParams.get("next");
      nav(_postLoginPath(next, cached), { replace: true });
      return;
    }
    const m = new URL(window.location.href).searchParams.get("magic");
    const em = new URL(window.location.href).searchParams.get("email");
    const next = new URL(window.location.href).searchParams.get("next");
    if (!m || !em || magicConsumed.current) return;
    magicConsumed.current = true;
    (async () => {
      try {
        const r = await axios.post(`${API}/public/partner/auth/login-otp`,
          { email: em, magic: m });
        if (r.data.access_token) {
          localStorage.setItem("rescigo_partner_token", r.data.access_token);
          _persistTraineeFromPartnerLogin(r.data);
          setPartnerUser(r.data.user);
          toast.success("Account activated · welcome!");
          nav(_postLoginPath(next, r.data.user), { replace: true });
        } else {
          setEmail(em); toast.message("Enter the OTP we just sent.");
          setChallengeId(r.data.challenge_id); setTtl(r.data.ttl_seconds || 600);
          setStep("otp");
        }
      } catch (e) { setErr(fmtErr(e)); }
    })();
  }, []);

  useEffect(() => {
    if (step !== "otp" || ttl <= 0) return;
    const id = setInterval(() => setTtl(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [step, ttl]);

  const sendOtp = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/public/partner/auth/login-otp`, { email: email.trim().toLowerCase() });
      setChallengeId(r.data.challenge_id); setTtl(r.data.ttl_seconds || 600);
      setOtp(""); setStep("otp");
    } catch (e) { setErr(fmtErr(e)); }
    setBusy(false);
  };
  const verifyOtp = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/public/partner/auth/login-verify`,
        { challenge_id: challengeId, otp: otp.trim() });
      localStorage.setItem("rescigo_partner_token", r.data.access_token);
      _persistTraineeFromPartnerLogin(r.data);
      setPartnerUser(r.data.user);
      toast.success("Welcome back");
      const next = new URL(window.location.href).searchParams.get("next");
      nav(_postLoginPath(next, r.data.user), { replace: true });
    } catch (e) { setErr(fmtErr(e)); }
    setBusy(false);
  };

  const mm = String(Math.floor(ttl / 60)).padStart(2, "0");
  const ss = String(ttl % 60).padStart(2, "0");

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0B2B66]" data-testid="partner-login">
      <div className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${BLUE}, #051434)` }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">Rescigo Academy</div>
          <div className="font-heading text-4xl mt-2">Partner Portal</div>
        </div>
        <div className="text-5xl font-heading leading-tight max-w-sm">
          Deliver <span style={{ color: GOLD }}>institutional</span>-grade BMW compliance training.
        </div>
        <div className="grid grid-cols-2 gap-6 max-w-sm text-xs opacity-80">
          <div><Users size={18} className="mb-2"/>Bulk onboard staff with CSV<br/>— track every learner</div>
          <div><Certificate size={18} className="mb-2"/>Co-branded certificates<br/>with your institutional logo</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>Institutional Access</div>
          <h1 className="font-heading text-3xl mt-1" style={{ color: BLUE }}>Sign in</h1>
          <p className="text-sm text-[#5C5A55] mt-1 mb-6">Access your partner dashboard with your registered email.</p>

          {step === "creds" && (
            <form onSubmit={sendOtp} className="space-y-3" data-testid="partner-creds-form">
              <LField Icon={Envelope} label="Registered email" type="email" value={email}
                       onChange={(e) => setEmail(e.target.value)} required autoFocus
                       testid="partner-email"/>
              {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
              <button type="submit" disabled={busy} data-testid="partner-send-otp"
                      className="w-full h-12 rounded-md text-white font-medium inline-flex items-center justify-center gap-2" style={{ background: BLUE }}>
                <Lightning size={13} weight="fill"/> {busy ? "Sending…" : "Send OTP"}
              </button>
            </form>
          )}
          {step === "otp" && (
            <form onSubmit={verifyOtp} className="space-y-3" data-testid="partner-otp-form">
              <div className="text-xs text-[#5C5A55]">OTP sent to <b>{email}</b> · Expires in <span className="font-mono">{mm}:{ss}</span></div>
              <LField Icon={LockKey} label="8-digit OTP" value={otp} mono
                       onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                       required autoFocus testid="partner-otp"/>
              {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
              <button type="submit" disabled={busy || otp.length !== 8} data-testid="partner-verify-otp"
                      className="w-full h-12 rounded-md text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: BLUE }}>
                Verify <ArrowRight size={13} weight="bold"/>
              </button>
              <button type="button" onClick={() => setStep("creds")}
                      className="w-full h-9 text-xs text-[#5C5A55] hover:text-[#0B2B66]">
                Use a different email
              </button>
            </form>
          )}
          <div className="mt-6 text-[11px] text-[#5C5A55] flex items-center gap-2">
            <ShieldCheck size={11} className="text-[#2D5A27]"/>
            Session is 8 hours · All access is audit-logged.
          </div>
        </div>
      </div>
    </div>
  );
}
const LField = ({ Icon, label, mono, testid, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55] block mb-1">{label}</label>
    <div className="relative">
      <Icon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A55]"/>
      <input {...props} data-testid={testid}
             className={`w-full h-11 pl-9 pr-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66] ${mono ? "font-mono text-center text-lg tracking-[0.3em]" : ""}`}/>
    </div>
  </div>
);

// Smart redirect helper: trainees go to the course catalog, admins to the
// partner dashboard. Honours an explicit ?next= query param.
const _postLoginPath = (queryNext, userObj) => {
  if (queryNext && queryNext.startsWith("/")) return queryNext;
  const role = userObj?.role || "";
  if (role === "partner_learner") return "/training/home";
  return "/partner-portal";
};

// When the partner-login response carries a parallel trainee_token (issued
// for `partner_learner` users), persist it so the existing /training/* surface
// works out-of-the-box for them.
const _persistTraineeFromPartnerLogin = (data) => {
  if (data?.trainee_token && data?.trainee) {
    localStorage.setItem("rescigo_trainee_token", data.trainee_token);
    localStorage.setItem("rescigo_trainee_user", JSON.stringify(data.trainee));
  }
};


// Same backend (public/partner/auth/login-otp + login-verify) but hero half
// renders the partner's logo + name; sign-in half is minimalist: email → OTP.
// No "I am signing in as…" selector — role is inferred from the email.
// ============================================================================
export function PartnerBrandedLogin() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [step, setStep] = useState("creds");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [ttl, setTtl] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const magicConsumed = React.useRef(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (localStorage.getItem("rescigo_partner_token")) {
      const cached = (() => {
        try { return JSON.parse(localStorage.getItem("rescigo_partner_user") || "null"); }
        catch { return null; }
      })();
      nav(_postLoginPath(url.searchParams.get("next"), cached), { replace: true });
      return;
    }
    axios.get(`${API}/public/partner/hospital/${slug}`)
      .then(r => setPartner(r.data.partner))
      .catch(e => setLoadErr(fmtErr(e)));

    // Auto-consume ?magic=&email= if present (trainee invitation link)
    const m = url.searchParams.get("magic");
    const em = url.searchParams.get("email");
    const next = url.searchParams.get("next");
    if (!m || !em || magicConsumed.current) return;
    magicConsumed.current = true;
    (async () => {
      try {
        const r = await axios.post(`${API}/public/partner/auth/login-otp`,
          { email: em, magic: m });
        if (r.data.access_token) {
          localStorage.setItem("rescigo_partner_token", r.data.access_token);
          _persistTraineeFromPartnerLogin(r.data);
          setPartnerUser(r.data.user);
          toast.success("You're in · let's start your first course!");
          nav(_postLoginPath(next, r.data.user), { replace: true });
        } else {
          setEmail(em); toast.message("Enter the OTP we just sent.");
          setChallengeId(r.data.challenge_id); setTtl(r.data.ttl_seconds || 600);
          setStep("otp");
        }
      } catch (ex) { setErr(fmtErr(ex)); }
    })();
  }, [slug, nav]);

  useEffect(() => {
    if (step !== "otp" || ttl <= 0) return;
    const id = setInterval(() => setTtl(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [step, ttl]);

  const sendOtp = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/public/partner/auth/login-otp`,
        { email: email.trim().toLowerCase(), partner_slug: slug });
      setChallengeId(r.data.challenge_id); setTtl(r.data.ttl_seconds || 600);
      setOtp(""); setStep("otp");
    } catch (ex) { setErr(fmtErr(ex)); }
    setBusy(false);
  };
  const verifyOtp = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/public/partner/auth/login-verify`,
        { challenge_id: challengeId, otp: otp.trim() });
      localStorage.setItem("rescigo_partner_token", r.data.access_token);
      _persistTraineeFromPartnerLogin(r.data);
      setPartnerUser(r.data.user);
      toast.success(`Welcome to ${partner?.name || "your Partner Portal"}`);
      const next = new URL(window.location.href).searchParams.get("next");
      nav(_postLoginPath(next, r.data.user), { replace: true });
    } catch (ex) { setErr(fmtErr(ex)); }
    setBusy(false);
  };

  if (loadErr) return <ErrorBlock msg={loadErr}/>;
  if (!partner) return <div className="min-h-screen grid place-items-center text-[#5C5A55]">Loading…</div>;

  const accent = partner.primary_color || BLUE;
  const mm = String(Math.floor(ttl / 60)).padStart(2, "0");
  const ss = String(ttl % 60).padStart(2, "0");

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="partner-branded-login">
      {/* Hero half */}
      <div className="relative flex flex-col text-white overflow-hidden min-h-[40vh] md:min-h-screen"
           style={{ background: `linear-gradient(135deg, ${accent}, #051434)` }}>
        {/* Rescigo master-brand mark (top-left) */}
        <div className="p-6 md:p-10 flex items-center gap-2.5">
          <img src="/brand/rescigo-logo-white.png" alt="Rescigo Academy"
               className="h-7 opacity-90"
               onError={(e) => { e.currentTarget.src = "/brand/rescigo-logo.png"; e.currentTarget.className = "h-7 bg-white rounded px-1.5 py-0.5"; }}/>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 border-l border-white/20 pl-2.5 ml-1 hidden sm:block">
            Powered by Rescigo Academy
          </div>
        </div>

        {/* Decorative ring */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/3 w-[520px] h-[520px] rounded-full border border-white/10 pointer-events-none"/>
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[380px] h-[380px] rounded-full border border-white/5 pointer-events-none"/>

        {/* Centered partner identity */}
        <div className="flex-1 flex items-center justify-center px-8 py-10 md:py-0 relative">
          <div className="text-center max-w-md">
            {partner.logo_url ? (
              <div className="w-36 h-36 md:w-44 md:h-44 mx-auto bg-white rounded-2xl shadow-2xl grid place-items-center p-6 ring-1 ring-white/20">
                <img src={partner.logo_url} alt={partner.name}
                     className="max-w-full max-h-full object-contain"
                     data-testid="partner-branded-logo"/>
              </div>
            ) : (
              <div className="w-36 h-36 md:w-44 md:h-44 mx-auto rounded-2xl bg-white/10 ring-1 ring-white/20 grid place-items-center shadow-2xl">
                <Buildings size={72} weight="duotone" className="text-white/80"/>
              </div>
            )}
            <h1 className="font-heading text-3xl md:text-4xl mt-8 leading-tight tracking-tight"
                data-testid="partner-branded-name">
              {partner.name}
            </h1>
            {partner.region && (
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 mt-3">{partner.region}</div>
            )}
            <div className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/70 bg-white/5 backdrop-blur px-3 py-1.5 rounded-full ring-1 ring-white/15">
              <Certificate size={11} weight="fill"/> Institutional Training Portal
            </div>
          </div>
        </div>

        {/* Compliance badges strip */}
        <div className="px-6 md:px-10 pb-6 md:pb-10 flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-white/50">
          <span className="flex items-center gap-1.5"><ShieldCheck size={11}/> BMW Rules 2016</span>
          <span className="opacity-50">·</span>
          <span>NABH aligned</span>
          <span className="opacity-50">·</span>
          <span>ISO 27001</span>
        </div>
      </div>

      {/* Sign-in half */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: accent }}>Staff sign-in</div>
          <h2 className="font-heading text-3xl mt-1.5 text-slate-900">Enter your email</h2>
          <p className="text-sm text-slate-500 mt-1.5 mb-8 leading-relaxed">
            We'll email you an 8-digit verification code.
          </p>

          {step === "creds" && (
            <form onSubmit={sendOtp} className="space-y-4" data-testid="branded-creds-form">
              <LField Icon={Envelope} label="Registered work email" type="email" value={email}
                       onChange={(e) => setEmail(e.target.value)} required autoFocus
                       placeholder="you@institution.org"
                       testid="branded-email"/>
              {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
              <button type="submit" disabled={busy || !email.trim()} data-testid="branded-send-code"
                      className="w-full h-12 rounded-md text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 transition"
                      style={{ background: accent }}>
                <Lightning size={13} weight="fill"/> {busy ? "Sending…" : "Send 8-digit code"}
              </button>
              <div className="pt-4 text-[11px] text-slate-500 leading-relaxed">
                Only staff members registered by <b style={{ color: accent }}>{partner.name}</b> can sign in here.
                If your email isn't registered, contact your institution's training admin.
              </div>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="branded-otp-form">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-700">
                Code sent to <b>{email}</b>
                <span className="ml-2 text-[10px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5">{mm}:{ss}</span>
              </div>
              <LField Icon={LockKey} label="8-digit code" value={otp} mono
                       onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                       required autoFocus inputMode="numeric" placeholder="— — — — — — — —"
                       testid="branded-otp"/>
              {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
              <button type="submit" disabled={busy || otp.length !== 8} data-testid="branded-verify-code"
                      className="w-full h-12 rounded-md text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 transition"
                      style={{ background: accent }}>
                Verify & sign in <ArrowRight size={13} weight="bold"/>
              </button>
              <button type="button" onClick={() => { setStep("creds"); setOtp(""); setErr(""); }}
                      className="w-full h-9 text-xs text-slate-500 hover:text-slate-800" data-testid="branded-change-email">
                ← Use a different email
              </button>
            </form>
          )}

          <div className="mt-10 pt-6 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1.5">
            <ShieldCheck size={11} className="text-[#2D5A27]"/>
            Session is 8 hours · All access audit-logged
          </div>
        </div>
      </div>
    </div>
  );
}



// ============================================================================
// Partner Layout
// ============================================================================
const NAV = [
  { to: "/partner-portal", label: "Overview", icon: Gauge, end: true },
  { to: "/partner-portal/users", label: "User Management", icon: Users },
  { to: "/partner-portal/programs", label: "Training Programs", icon: GraduationCap },
  { to: "/partner-portal/progress", label: "Course Progress", icon: ChartBar },
  { to: "/partner-portal/certificates", label: "Certification Center", icon: Certificate },
  { to: "/partner-portal/branding", label: "Co-Branding", icon: Palette },
  { to: "/partner-portal/billing", label: "Billing", icon: Receipt },
  { to: "/partner-portal/reports", label: "Reports", icon: FileText },
  { to: "/partner-portal/audit", label: "Audit Logs", icon: ShieldCheck },
];

export function PartnerLayout() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("rescigo_partner_token")) {
      nav("/partner/login", { replace: true }); return;
    }
    papi.get("/partner/me").then(r => {
      // Trainees never enter the admin portal — bounce to course catalog.
      if (r.data?.user?.role === "partner_learner") {
        nav("/training/home", { replace: true }); return;
      }
      setMe(r.data);
    }).catch(() => { clearPartnerAuth(); nav("/partner/login", { replace: true }); });
  }, []);
  const logout = () => { clearPartnerAuth(); nav("/partner/login"); };
  if (!me) return <div className="p-12 text-center text-[#5C5A55]">Loading…</div>;
  const primary = me.partner?.primary_color || BLUE;
  return (
    <LocationGate
      pingPath="/api/findgps/ping/partner"
      tokenKey="rescigo_partner_token"
      bodyShape="universal"
      label="Rescigo Partner"
      onRevoked={() => { try { clearPartnerAuth(); } catch { /* noop */ } }}
    >
    <div className="min-h-screen bg-[#F7F6F2] flex" data-testid="partner-portal">
      <aside className="w-64 bg-white border-r border-[#0B2B66]/10 flex flex-col">
        <div className="p-5 border-b border-[#0B2B66]/10">
          <div className="flex items-center gap-3">
            {me.partner?.logo_url ? (
              <img src={me.partner.logo_url} alt="" className="w-10 h-10 rounded object-contain bg-white border border-[#0B2B66]/10"/>
            ) : (
              <div className="w-10 h-10 rounded grid place-items-center text-white text-xs font-bold" style={{ background: primary }}>
                {me.partner?.name?.[0] || "P"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm leading-tight truncate" style={{ color: primary }}>{me.partner?.name}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#5C5A55]">{me.partner?.tier} · {me.partner?.category?.replace(/_/g, " ")}</div>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-0.5 flex-1">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`partner-nav-${n.to.replace(/\//g, "-")}`}
                     className={({ isActive }) =>
                       `flex items-center gap-3 h-10 px-3 rounded-md text-sm transition ${
                         isActive ? "text-white" : "text-[#5C5A55] hover:bg-[#0B2B66]/5"
                       }`}
                     style={({ isActive }) => isActive ? { background: primary } : {}}>
              <n.icon size={14} weight="duotone"/> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#0B2B66]/10 text-xs">
          <div className="font-medium">{me.user.name}</div>
          <div className="text-[10px] text-[#5C5A55]">{me.user.email}</div>
          <button onClick={logout} data-testid="partner-logout"
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 h-8 rounded-md border border-[#0B2B66]/20 text-[#5C5A55] hover:bg-[#0B2B66]/5 text-xs">
            <SignOut size={11}/> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet context={{ me, primary }}/>
      </main>
    </div>
    </LocationGate>
  );
}

// ============================================================================
// Partner Pages
// ============================================================================
export function PartnerOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { papi.get("/partner/dashboard").then(r => setD(r.data)).catch(e => toast.error(fmtErr(e))); }, []);
  return (
    <Page title="Overview" sub="Institution-wide training & compliance snapshot">
      {!d ? <Skel/> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Total Users" v={d.total_users} Icon={Users} accent={BLUE}/>
            <Kpi label="Active Learners" v={d.active_users} Icon={Users} accent={GREEN}/>
            <Kpi label="Pending" v={d.pending_users} Icon={Users}/>
            <Kpi label="Completion" v={`${d.completion_pct}%`} Icon={ChartBar} accent={GREEN}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <Kpi label="Enrollments" v={d.total_enrollments} Icon={GraduationCap}/>
            <Kpi label="In progress" v={d.in_progress} Icon={GraduationCap} accent={GOLD}/>
            <Kpi label="Certificates" v={d.certificate_count} Icon={Certificate} accent={BLUE}/>
            <Kpi label="Expiring 30d" v={d.expiring_30d} Icon={Certificate} accent={RED}/>
          </div>
        </>
      )}
    </Page>
  );
}

export function PartnerUsers() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(null);
  const load = async () => {
    try { const r = await papi.get("/partner/users"); setRows(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);
  const save = async (body) => {
    try { await papi.post("/partner/users", body); toast.success("Added"); setAdding(null); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <Page title="User Management" sub={`${rows.length} user(s)`}
          action={<button onClick={() => setAdding({ name: "", email: "", phone: "", department: "", role: "partner_learner" })}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm" style={{ background: BLUE }}
                            data-testid="pu-add">
            <Users size={13}/> Add user</button>}>
      <div className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Role</th><th className="p-3 text-left">Department</th>
              <th className="p-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.id} className="border-b border-[#0B2B66]/5 text-xs" data-testid={`pu-row-${u.id}`}>
                <td className="p-3 font-medium">{u.name}</td><td className="p-3">{u.email}</td>
                <td className="p-3 uppercase text-[9px] tracking-[0.15em] text-[#5C5A55]">{(u.role || "").replace(/_/g, " ")}</td>
                <td className="p-3">{u.department || "—"}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold ${u.status === "active" ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#F4C7B9] text-[#7A2E18]"}`}>{u.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-[#5C5A55] italic">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {adding && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="font-heading text-lg mb-4" style={{ color: BLUE }}>Add User</h3>
            <form onSubmit={(e) => { e.preventDefault(); save(adding); }} className="space-y-3">
              <Pi label="Name" v={adding.name} on={(v) => setAdding({ ...adding, name: v })} required testid="pu-name"/>
              <Pi label="Email" type="email" v={adding.email} on={(v) => setAdding({ ...adding, email: v })} required testid="pu-email"/>
              <Pi label="Phone" v={adding.phone} on={(v) => setAdding({ ...adding, phone: v })}/>
              <Pi label="Department" v={adding.department} on={(v) => setAdding({ ...adding, department: v })}/>
              <Ps label="Role" value={adding.role} onChange={(v) => setAdding({ ...adding, role: v })}
                  opts={[["partner_learner", "Learner"], ["partner_manager", "Manager"], ["partner_admin", "Admin"]]}/>
              <div className="flex gap-2 pt-3 border-t border-[#0B2B66]/10">
                <button type="submit" className="h-10 px-4 rounded-md text-white text-sm" style={{ background: BLUE }} data-testid="pu-save">Save</button>
                <button type="button" onClick={() => setAdding(null)} className="h-10 px-3 text-sm text-[#5C5A55]">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Page>
  );
}

export function PartnerPrograms() {
  const [rows, setRows] = useState([]);
  useEffect(() => { papi.get("/partner/programs").then(r => setRows(r.data)).catch(e => toast.error(fmtErr(e))); }, []);
  return (
    <Page title="Training Programs" sub="Courses available to your institution">
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map(c => (
          <div key={c.id} className="bg-white rounded-xl border border-[#0B2B66]/10 p-5" data-testid={`pp-${c.id}`}>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>{c.audience}</div>
            <h3 className="font-heading text-lg mt-1" style={{ color: BLUE }}>{c.name}</h3>
            <p className="text-xs text-[#5C5A55] mt-1">{c.description}</p>
            <div className="mt-3 flex items-center gap-3 text-xs"><span>{c.duration_minutes} min</span>·<span>{c.pass_threshold}% to pass</span>·<span>{c.is_paid ? `₹${c.price_paise/100}` : "Free"}</span></div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-2 text-center py-12 text-[#5C5A55]">No programs assigned yet. Contact your Rescigo admin.</div>}
      </div>
    </Page>
  );
}

export function PartnerProgress() {
  const [rows, setRows] = useState([]);
  useEffect(() => { papi.get("/partner/progress").then(r => setRows(r.data)).catch(e => toast.error(fmtErr(e))); }, []);
  return (
    <Page title="Course Progress" sub={`${rows.length} enrollments`}>
      <div className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
            <th className="p-3 text-left">Learner</th><th className="p-3 text-left">Course</th>
            <th className="p-3 text-left">Status</th><th className="p-3 text-left">Progress</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-[#0B2B66]/5 text-xs">
                <td className="p-3">{r.trainee_email}</td><td className="p-3">{r.course_name}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold bg-[#EAF1FD] text-[#0B2B66]">{r.status}</span></td>
                <td className="p-3">
                  <div className="flex items-center gap-2"><div className="flex-1 h-1.5 rounded-full bg-[#0B2B66]/10 overflow-hidden">
                    <div className="h-full" style={{ width: `${r.progress_pct || 0}%`, background: GREEN }}/></div>
                    <span className="text-[10px] font-mono w-10 text-right">{r.progress_pct || 0}%</span></div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-[#5C5A55] italic">No enrollments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Page>
  );
}

export function PartnerCertificates() {
  const [rows, setRows] = useState([]);
  useEffect(() => { papi.get("/partner/certificates").then(r => setRows(r.data)).catch(e => toast.error(fmtErr(e))); }, []);
  return (
    <Page title="Certification Center" sub={`${rows.length} certificates issued`}>
      <div className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
            <th className="p-3 text-left">Cert No</th><th className="p-3 text-left">Recipient</th>
            <th className="p-3 text-left">Course</th><th className="p-3 text-left">Valid Until</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} className="border-b border-[#0B2B66]/5 text-xs">
                <td className="p-3 font-mono">{c.certificate_no}</td><td className="p-3">{c.recipient_name}<div className="text-[10px] text-[#5C5A55]">{c.recipient_email}</div></td>
                <td className="p-3">{c.course_name || "—"}</td><td className="p-3">{c.valid_until}</td>
                <td className="p-3 text-right"><a href={`${API}/training/certificate/${c.certificate_no}.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B2B66] hover:underline"><DownloadSimple size={11}/> PDF</a></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-[#5C5A55] italic">No certificates issued yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Page>
  );
}

export function PartnerBranding() {
  const [b, setB] = useState(null);
  useEffect(() => { papi.get("/partner/branding").then(r => setB(r.data)).catch(e => toast.error(fmtErr(e))); }, []);
  const save = async () => {
    try {
      await papi.put("/partner/branding", {
        logo_url: b.logo_url, partner_logo_1_url: b.partner_logo_1_url,
        partner_logo_2_url: b.partner_logo_2_url, primary_color: b.primary_color,
        banner_url: b.banner_url, intro_message: b.intro_message, website: b.website,
      });
      toast.success("Branding saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  if (!b) return <Skel/>;
  return (
    <Page title="Co-Branding" sub="Customize how your institution appears on the training academy"
          action={<button onClick={save} className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm" style={{ background: BLUE }} data-testid="branding-save">Save</button>}>
      <div className="bg-white rounded-xl border border-[#0B2B66]/10 p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <Pi label="Primary Logo URL" v={b.logo_url} on={(v) => setB({ ...b, logo_url: v })}/>
          <Pi label="Banner URL" v={b.banner_url} on={(v) => setB({ ...b, banner_url: v })}/>
          <Pi label="Partner Logo 1 URL" v={b.partner_logo_1_url} on={(v) => setB({ ...b, partner_logo_1_url: v })}/>
          <Pi label="Partner Logo 2 URL" v={b.partner_logo_2_url} on={(v) => setB({ ...b, partner_logo_2_url: v })}/>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">Primary Color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={b.primary_color || "#0B2B66"} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="w-12 h-10 border border-[#0B2B66]/20 rounded cursor-pointer"/>
              <input type="text" value={b.primary_color || ""} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="flex-1 h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none font-mono"/>
            </div>
          </div>
          <Pi label="Website" v={b.website} on={(v) => setB({ ...b, website: v })}/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">Intro Message</label>
          <textarea value={b.intro_message || ""} onChange={(e) => setB({ ...b, intro_message: e.target.value })} rows={3}
                    className="w-full px-3 py-2 border border-[#0B2B66]/20 rounded-md text-sm outline-none"/>
        </div>
        <div className="pt-4 border-t border-[#0B2B66]/10">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5C5A55] mb-2">Public Landing Page</div>
          <a href={`/training/hospital/${b.slug}`} target="_blank" rel="noreferrer" className="text-sm text-[#0B2B66] hover:underline inline-flex items-center gap-1">
            /training/hospital/{b.slug} <ArrowRight size={11}/>
          </a>
        </div>
      </div>
    </Page>
  );
}

export function PartnerBilling() {
  return (
    <Page title="Billing" sub="Invoices and tier management">
      <div className="bg-white rounded-xl border border-[#0B2B66]/10 p-8 text-center">
        <Receipt size={32} className="mx-auto text-[#5C5A55] mb-3"/>
        <h3 className="font-heading text-lg" style={{ color: BLUE }}>Manual invoicing model</h3>
        <p className="text-sm text-[#5C5A55] max-w-md mx-auto mt-2">
          Your Rescigo account manager will issue monthly invoices via email. Contact billing@rescigo.health for quotes, upgrades, or payment receipts.
        </p>
      </div>
    </Page>
  );
}

export function PartnerReports() {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null); // string of currently downloading id
  const tok = typeof window !== "undefined"
    ? localStorage.getItem("rescigo_partner_token") : null;

  useEffect(() => {
    papi.get("/partner/reports/summary")
      .then(r => setSummary(r.data))
      .catch(e => toast.error(fmtErr(e)));
  }, []);

  const download = async (kind, format) => {
    const id = `${kind}-${format}`;
    setBusy(id);
    try {
      const res = await fetch(
        `${API}/partner/reports/${kind}.${format}`,
        { headers: { Authorization: `Bearer ${tok}` } },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-report.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind} ${format.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.message || "Download failed"); }
    setBusy(null);
  };

  return (
    <Page title="Reports" sub="Export learner progress and partner compliance data">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="reports-summary">
        <Kpi label="Enrollments" v={summary?.progress_count} Icon={GraduationCap}/>
        <Kpi label="In Progress" v={summary?.in_progress} Icon={ChartBar} accent={GOLD}/>
        <Kpi label="Passed" v={summary?.passed} Icon={ShieldCheck} accent={GREEN}/>
        <Kpi label="Certificates" v={summary?.compliance_count} Icon={Certificate} accent={BLUE}/>
        <Kpi label="Active Certs" v={summary?.active_certs} Icon={Certificate} accent={GREEN}/>
      </div>

      {/* Two report cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <ReportCard
          title="Course Progress"
          desc="Per-learner enrollment, status, completion date, and certificate number."
          Icon={GraduationCap}
          accent={BLUE}
          rowsLabel={`${summary?.progress_count ?? 0} learners`}
          onCSV={() => download("progress", "csv")}
          onPDF={() => download("progress", "pdf")}
          busy={busy}
          kind="progress"
        />
        <ReportCard
          title="Compliance Certificates"
          desc="All issued certificates with verification URLs, validity, and revocation status. Co-branded PDF for regulator-ready submission."
          Icon={Certificate}
          accent={GOLD}
          rowsLabel={`${summary?.compliance_count ?? 0} certificates`}
          onCSV={() => download("compliance", "csv")}
          onPDF={() => download("compliance", "pdf")}
          busy={busy}
          kind="compliance"
        />
      </div>

      <div className="text-[11px] text-[#5C5A55] italic">
        PDFs include your partner logo, primary colour and a confidentiality footer — share directly with auditors.
      </div>
    </Page>
  );
}

function ReportCard({ title, desc, Icon, accent, rowsLabel,
                       onCSV, onPDF, busy, kind }) {
  const csvBusy = busy === `${kind}-csv`;
  const pdfBusy = busy === `${kind}-pdf`;
  return (
    <div className="bg-white rounded-xl border border-[#0B2B66]/10 p-6"
          data-testid={`report-card-${kind}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg grid place-items-center flex-none"
              style={{ background: `${accent}14`, color: accent }}>
          <Icon size={18} weight="duotone"/>
        </div>
        <div className="flex-1">
          <div className="font-heading text-lg" style={{ color: BLUE }}>{title}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mt-0.5">{rowsLabel}</div>
        </div>
      </div>
      <p className="text-sm text-[#5C5A55] mt-3 leading-relaxed">{desc}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={onCSV} disabled={csvBusy}
                data-testid={`btn-${kind}-csv`}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#0B2B66]/20 text-sm text-[#0B2B66] hover:bg-[#0B2B66]/5 disabled:opacity-60">
          <DownloadSimple size={13} weight="bold"/>
          {csvBusy ? "Preparing…" : "Download CSV"}
        </button>
        <button onClick={onPDF} disabled={pdfBusy}
                data-testid={`btn-${kind}-pdf`}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm text-white disabled:opacity-60"
                style={{ background: BLUE }}>
          <FileText size={13} weight="bold"/>
          {pdfBusy ? "Preparing…" : "Download PDF"}
        </button>
      </div>
    </div>
  );
}

export function PartnerAudit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    papi.get("/partner/audit-logs")
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter(r =>
    !filter ||
    (r.action || "").toLowerCase().includes(filter.toLowerCase()) ||
    (r.actor_email || "").toLowerCase().includes(filter.toLowerCase()),
  );

  const formatMeta = (m) => {
    if (!m || typeof m !== "object") return "";
    const keys = Object.keys(m);
    if (!keys.length) return "";
    return keys.slice(0, 3).map(k => `${k}: ${m[k]}`).join(" · ");
  };

  return (
    <Page title="Audit Logs"
          sub="Every change made to your partner account, exportable on request"
          action={
            <input value={filter} onChange={e => setFilter(e.target.value)}
                    placeholder="Filter by action or actor…"
                    data-testid="audit-filter"
                    className="h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm w-72"/>
          }>
      <div className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden"
            data-testid="audit-table">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#5C5A55]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5A55] italic">
            {rows.length === 0 ? "No audit events yet." : "No events match your filter."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0B2B66]/5 text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">When</th>
                <th className="text-left px-4 py-3 font-medium">Action</th>
                <th className="text-left px-4 py-3 font-medium">Actor</th>
                <th className="text-left px-4 py-3 font-medium">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0B2B66]/5">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-[#F7F6F2]/40">
                  <td className="px-4 py-3 font-mono text-[11px] text-[#5C5A55] whitespace-nowrap">
                    {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#0B2B66]/8 text-[#0B2B66]">
                      {r.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#1A1A1A]">{r.actor_email || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#5C5A55]">{formatMeta(r.meta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="text-[11px] text-[#5C5A55] italic">
        Showing {filtered.length} of {rows.length} most-recent events.
      </div>
    </Page>
  );
}

// ============================================================================
// PUBLIC CO-BRANDED LANDING PAGE /training/hospital/:slug
// ============================================================================
export function HospitalLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/partner/hospital/${slug}`)
      .then(r => setData(r.data))
      .catch(e => setErr(fmtErr(e)));
  }, [slug]);
  if (err) return <ErrorBlock msg={err}/>;
  if (!data) return <div className="p-12 text-center text-[#5C5A55]">Loading…</div>;
  const p = data.partner;
  const accent = p.primary_color || BLUE;
  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="hospital-landing"
         style={{ "--brand": accent }}>
      {/* Top nav */}
      <div className="border-b border-[#0B2B66]/10 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {p.logo_url && <img src={p.logo_url} alt="" className="h-10"/>}
            <div>
              <div className="font-heading text-lg leading-tight" style={{ color: accent }}>{p.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Powered by Rescigo Academy</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {(p.partner_logo_1_url || p.partner_logo_2_url) && (
              <div className="flex items-center gap-2 pr-3 border-r border-[#0B2B66]/10">
                {p.partner_logo_1_url && <img src={p.partner_logo_1_url} alt="" className="h-8"/>}
                {p.partner_logo_2_url && <img src={p.partner_logo_2_url} alt="" className="h-8"/>}
              </div>
            )}
            <Link to={`/training/hospital/${slug}/login`} className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: accent }} data-testid="hl-login">
              Staff sign-in <ArrowRight size={11}/>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative text-white py-20 px-6 overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${accent}, #051434)` }}>
        {p.banner_url && (
          <img src={p.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20"/>
        )}
        <div className="relative max-w-6xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold opacity-80">{p.category?.replace(/_/g, " ")} · {p.region || "India"}</div>
          <h1 className="font-heading text-5xl mt-4 max-w-3xl leading-tight">
            BMW Compliance Training for <span style={{ color: GOLD }}>{p.name}</span>
          </h1>
          {p.intro_message && <p className="text-lg opacity-90 max-w-2xl mt-5">{p.intro_message}</p>}
          <div className="mt-8 flex items-center gap-3">
            <Link to={`/training/hospital/${slug}/login`} className="inline-flex items-center gap-2 h-12 px-6 rounded-md text-sm font-medium bg-white" style={{ color: accent }} data-testid="hl-cta">
              Start training <ArrowRight size={12} weight="bold"/>
            </Link>
            <span className="text-sm opacity-75">{data.course_count} course(s) ready</span>
          </div>
        </div>
      </div>

      {/* Courses */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="font-heading text-2xl mb-6" style={{ color: accent }}>Assigned Training Programs</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {data.courses.map(c => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                         className="bg-white rounded-xl border border-[#0B2B66]/10 p-6" data-testid={`hl-course-${c.id}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>{c.audience}</div>
              <h3 className="font-heading text-xl mt-2" style={{ color: accent }}>{c.name}</h3>
              <p className="text-sm text-[#5C5A55] mt-2">{c.description}</p>
              <div className="mt-4 flex items-center gap-3 text-xs text-[#5C5A55]">
                <span>⏱ {c.duration_minutes} min</span>·<span>🎯 {c.pass_threshold}% to pass</span>·<span>{c.is_paid ? `₹${c.price_paise/100}` : "Free"}</span>
              </div>
            </motion.div>
          ))}
          {data.courses.length === 0 && (
            <div className="col-span-2 text-center py-12 text-[#5C5A55]">No courses assigned yet. Contact your compliance officer.</div>
          )}
        </div>
      </div>

      {/* Compliance messaging */}
      <div className="bg-white border-t border-[#0B2B66]/10">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <ShieldCheck size={24} style={{ color: accent }}/>
            <h3 className="font-heading text-lg mt-3" style={{ color: accent }}>BMW Rules 2016 Compliant</h3>
            <p className="text-sm text-[#5C5A55] mt-2">Every program is aligned to the Bio-Medical Waste Management Rules, 2016 and audited for regulatory relevance.</p>
          </div>
          <div>
            <Certificate size={24} style={{ color: accent }}/>
            <h3 className="font-heading text-lg mt-3" style={{ color: accent }}>Co-branded Certificates</h3>
            <p className="text-sm text-[#5C5A55] mt-2">Every graduate receives a QR-verifiable certificate featuring {p.name} branding alongside Rescigo's authority seal.</p>
          </div>
          <div>
            <Buildings size={24} style={{ color: accent }}/>
            <h3 className="font-heading text-lg mt-3" style={{ color: accent }}>Institution-wide Analytics</h3>
            <p className="text-sm text-[#5C5A55] mt-2">Your compliance officer gets a private dashboard tracking every learner, department-wise performance, and expiring certificates.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0B2B66] text-white py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs opacity-80">
          <div>© {new Date().getFullYear()} {p.name} · Training delivered by Rescigo Academy</div>
          <div className="flex items-center gap-4">
            {p.contact_email && <a href={`mailto:${p.contact_email}`}>{p.contact_email}</a>}
            {p.compliance_officer_name && <span>Compliance Officer: {p.compliance_officer_name}</span>}
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================================
// Primitives
// ============================================================================
const Page = ({ title, sub, action, children }) => (
  <div className="p-6 md:p-8 space-y-6">
    <div className="flex items-start justify-between flex-wrap gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55]">Partner Portal</div>
        <h1 className="font-heading text-3xl mt-1" style={{ color: BLUE }}>{title}</h1>
        {sub && <p className="text-sm text-[#5C5A55] mt-1">{sub}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);
const Kpi = ({ label, v, Icon, accent }) => (
  <div className="bg-white rounded-lg border border-[#0B2B66]/10 p-4 relative overflow-hidden">
    <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full opacity-5" style={{ background: accent || BLUE }}/>
    <Icon size={16} weight="duotone" color={accent || BLUE}/>
    <div className="font-heading text-2xl mt-2" style={{ color: accent || BLUE }}>{v ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mt-0.5">{label}</div>
  </div>
);
const Skel = () => <div className="p-12 text-center text-[#5C5A55]">Loading…</div>;
const Pi = ({ label, v, on, type = "text", required, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <input type={type} value={v || ""} onChange={(e) => on(e.target.value)} required={required} data-testid={testid}
           className="w-full h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
  </div>
);
const Ps = ({ label, value, onChange, opts }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm bg-white outline-none">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
const ErrorBlock = ({ msg }) => (
  <div className="min-h-screen grid place-items-center p-8 bg-[#F7F6F2]">
    <div className="bg-white rounded-xl border border-red-200 p-8 max-w-md text-center">
      <div className="text-sm text-red-600 mb-4">{msg}</div>
      <Link to="/training" className="text-sm text-[#0B2B66] hover:underline">← Back to Academy</Link>
    </div>
  </div>
);
